import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

const DataProcessingAddendum = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <Link 
          to="/" 
          className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Back to home
        </Link>

        <h1 className="text-4xl font-bold mb-2">Data Processing Addendum</h1>
        <p className="text-xl font-semibold text-muted-foreground mb-2">Wololo Workforce Hub</p>
        <p className="text-sm text-muted-foreground mb-12">Effective Date: 4 April 2026 · Version 1.0</p>

        <div className="mb-8 p-4 bg-muted/50 rounded-lg text-sm text-muted-foreground">
          <p>This DPA applies with the Wololo Terms of Service and any applicable Order Form. It governs Processing of Personal Data by Wololo on behalf of Customer in connection with the Service.</p>
        </div>

        <div className="space-y-8 text-foreground">
          {/* Section 1 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">1. Scope and Order of Precedence</h2>
            <p className="text-muted-foreground leading-relaxed">
              1.1 This DPA applies only to Processing of Personal Data by Wololo on behalf of Customer in connection with the Service.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              1.2 This DPA does not apply to Processing for which Wololo acts as an independent controller, including limited Processing of business contact, account, billing, security, fraud-prevention, support, and legal-compliance data that Wololo needs for its own legitimate business operations.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              1.3 In the event of conflict, the following order of precedence applies:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-2 ml-4">
              <li>any signed Order Form or negotiated amendment that expressly overrides this DPA;</li>
              <li>this DPA, including its Annexes;</li>
              <li>the Terms.</li>
            </ul>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">2. Definitions</h2>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li><strong className="text-foreground">"Applicable Data Protection Law"</strong> means all laws and regulations applicable to the Processing of Personal Data under the Agreement, including, where applicable, the GDPR, UK GDPR, Swiss data protection law, and Indonesia's Personal Data Protection Law.</li>
              <li><strong className="text-foreground">"Controller"</strong>, <strong className="text-foreground">"Processor"</strong>, <strong className="text-foreground">"Data Subject"</strong>, <strong className="text-foreground">"Personal Data"</strong>, <strong className="text-foreground">"Personal Data Breach"</strong>, <strong className="text-foreground">"Process"</strong> and <strong className="text-foreground">"Subprocessor"</strong> have the meanings given to them under Applicable Data Protection Law.</li>
              <li><strong className="text-foreground">"Security Incident"</strong> means a confirmed breach of security leading to accidental or unlawful destruction, loss, alteration, unauthorized disclosure of, or access to, Customer Personal Data Processed by Wololo. Security Incident does not include unsuccessful attempts such as pings, port scans, denial-of-service attempts, malware blocked in transit, or failed login attempts.</li>
              <li><strong className="text-foreground">"Customer Personal Data"</strong> means Personal Data Processed by Wololo on behalf of Customer under the Agreement.</li>
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">3. Roles of the Parties</h2>
            <p className="text-muted-foreground leading-relaxed">
              3.1 The parties acknowledge that, with respect to Customer Personal Data:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-2 ml-4">
              <li>Customer is the Controller or, where relevant, another processor acting on documented instructions of the relevant controller; and</li>
              <li>Wololo is the Processor.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              3.2 Customer is responsible for:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-2 ml-4">
              <li>determining whether the Service is appropriate for its intended use cases;</li>
              <li>providing all notices and obtaining all consents, authorizations, approvals, and other lawful bases required for the Processing of Customer Personal Data;</li>
              <li>ensuring that its instructions to Wololo comply with Applicable Data Protection Law; and</li>
              <li>using the Service in a way that is consistent with the Agreement and Applicable Data Protection Law, including employment, monitoring, payroll, and workplace-location rules.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              3.3 Customer acknowledges that the Service includes workforce management features that may involve employee profile data, scheduling, attendance, geolocation capture during clock-in and clock-out events, leave management, payroll, notifications, and related audit trails. Customer is solely responsible for deciding which features to enable and what categories of Personal Data to submit.
            </p>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">4. Customer Instructions</h2>
            <p className="text-muted-foreground leading-relaxed">
              4.1 Wololo shall Process Customer Personal Data only:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-2 ml-4">
              <li>to provide, secure, support, maintain, and improve the Service in accordance with the Agreement;</li>
              <li>on documented instructions from Customer, including Customer's configuration of the Service and use of its features; and</li>
              <li>as otherwise required by applicable law.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              4.2 The Agreement, this DPA, Customer's account configuration, and Customer's use of Service functionality constitute Customer's complete documented instructions to Wololo for the Processing of Customer Personal Data, unless the parties agree additional instructions in writing.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              4.3 If Wololo believes an instruction infringes Applicable Data Protection Law, Wololo may inform Customer and suspend the relevant Processing until the issue is resolved.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              4.4 Wololo is not required to follow an instruction that would violate law, create a security risk, or materially change the nature or scope of the Service without mutual written agreement.
            </p>
          </section>

          {/* Section 5 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">5. Processor Obligations</h2>
            <p className="text-muted-foreground leading-relaxed">
              5.1 Wololo shall:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-2 ml-4">
              <li>Process Customer Personal Data only on Customer's documented instructions, except where otherwise required by law;</li>
              <li>ensure that personnel authorized to Process Customer Personal Data are subject to appropriate confidentiality obligations;</li>
              <li>implement and maintain appropriate technical and organizational measures designed to protect Customer Personal Data;</li>
              <li>provide reasonable assistance to Customer as described in this DPA; and</li>
              <li>make available information reasonably necessary to demonstrate compliance with this DPA, subject to Section 11.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              5.2 Where Wololo is required by law to Process Customer Personal Data other than on Customer's instructions, Wololo shall, unless prohibited by law, inform Customer before doing so.
            </p>
          </section>

          {/* Section 6 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">6. Confidentiality</h2>
            <p className="text-muted-foreground leading-relaxed">
              6.1 Wololo shall ensure that persons authorized to Process Customer Personal Data are bound by confidentiality obligations or are under an appropriate statutory obligation of confidentiality.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              6.2 Customer shall treat as confidential any non-public information it receives from Wololo about Wololo's security measures, audit materials, and Subprocessors.
            </p>
          </section>

          {/* Section 7 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">7. Security Measures</h2>
            <p className="text-muted-foreground leading-relaxed">
              7.1 Wololo shall implement and maintain appropriate technical and organizational measures, including the measures described in Annex II.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              7.2 Customer acknowledges that no security measure is absolute. Wololo does not warrant that the Service will be immune from all security threats, but will maintain measures designed to reduce reasonably foreseeable risks.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              7.3 Customer is responsible for:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-2 ml-4">
              <li>maintaining the confidentiality of its own credentials and administrative access;</li>
              <li>assigning permissions appropriately within the Service;</li>
              <li>enabling and managing lawful workplace policies for attendance, location, payroll, and notifications; and</li>
              <li>reviewing whether public-facing or user-visible content configured by Customer is appropriate for Customer's own compliance obligations.</li>
            </ul>
          </section>

          {/* Section 8 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">8. Security Incidents</h2>
            <p className="text-muted-foreground leading-relaxed">
              8.1 Wololo shall notify Customer without undue delay after becoming aware of a confirmed Security Incident affecting Customer Personal Data.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              8.2 Such notification will, to the extent reasonably available at the time:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-2 ml-4">
              <li>describe the nature of the Security Incident;</li>
              <li>describe the categories of affected Customer Personal Data;</li>
              <li>describe measures taken or proposed to address the Security Incident; and</li>
              <li>provide information reasonably necessary for Customer to meet its own legal obligations.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              8.3 Wololo's notification of, or response to, a Security Incident shall not be construed as an admission of fault or liability.
            </p>
          </section>

          {/* Section 9 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">9. Assistance with Data Subject Requests, DPIAs, and Regulators</h2>
            <p className="text-muted-foreground leading-relaxed">
              9.1 Wololo shall provide reasonable assistance to Customer to enable Customer to respond to requests from Data Subjects exercising their rights under Applicable Data Protection Law.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              9.2 If Wololo receives a request directly from a Data Subject relating to Customer Personal Data, Wololo may:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-2 ml-4">
              <li>advise the Data Subject to submit the request to Customer;</li>
              <li>notify Customer of the request, unless prohibited by law; and</li>
              <li>not respond substantively except as required by law or as authorized by Customer.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              9.3 Wololo shall provide reasonable assistance to Customer with data protection impact assessments, prior consultations with supervisory authorities, and Customer's own security and breach assessment obligations, in each case only to the extent required by Applicable Data Protection Law and only where such assistance relates to Processing under this DPA.
            </p>
          </section>

          {/* Section 10 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">10. Subprocessors</h2>
            <p className="text-muted-foreground leading-relaxed">
              10.1 Customer authorizes Wololo to engage Subprocessors in accordance with this Section.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              10.2 Wololo shall:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-2 ml-4">
              <li>impose data protection obligations on Subprocessors that are no less protective than the obligations in this DPA; and</li>
              <li>remain responsible for the performance of its Subprocessors to the extent required by Applicable Data Protection Law.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              10.3 A current list of Subprocessors known from the Service architecture as of the Effective Date is set out in Annex III.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              10.4 Wololo may update its Subprocessor list from time to time. Where required by Applicable Data Protection Law, Wololo shall provide a mechanism for Customer to raise a reasonable objection to a new Subprocessor on data protection grounds. If the parties cannot resolve such objection within a reasonable period, Wololo may terminate the affected functionality or, if reasonably necessary, the relevant Order Form.
            </p>
          </section>

          {/* Section 11 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">11. Audit and Compliance Information</h2>
            <p className="text-muted-foreground leading-relaxed">
              11.1 On written request, Wololo shall make available to Customer information reasonably necessary to demonstrate compliance with this DPA, including high-level summaries of security measures and Subprocessor information.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              11.2 Customer may request an audit no more than once in any rolling twelve-month period, subject to reasonable prior written notice, scope limitation to matters relevant to this DPA, occurrence during normal business hours, protection of Wololo's confidential information, and exclusion of other customers' data, source code, and vulnerability details. Customer shall bear its own costs and reimburse Wololo for reasonable internal costs where the audit is not required by law or reveals no material non-compliance.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              11.3 Wololo may satisfy audit obligations through recent third-party audit reports, security documentation, questionnaires, or comparable materials where appropriate.
            </p>
          </section>

          {/* Section 12 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">12. International Data Transfers</h2>
            <p className="text-muted-foreground leading-relaxed">
              12.1 Customer authorizes Wololo and its Subprocessors to Process Customer Personal Data in any jurisdiction where Wololo, its affiliates, or its Subprocessors maintain operations, provided that such Processing complies with Applicable Data Protection Law.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              12.2 Where Customer Personal Data originating from the EEA, UK, or Switzerland is transferred to a country that is not the subject of an adequacy decision, the parties shall be deemed to have entered into the applicable Standard Contractual Clauses or equivalent approved transfer mechanism.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              12.3 The SCCs or other approved transfer mechanism are incorporated by reference and completed using the Annexes to this DPA. If the SCCs conflict with this DPA, the SCCs prevail to the extent of the conflict.
            </p>
          </section>

          {/* Section 13 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">13. Return and Deletion of Customer Personal Data</h2>
            <p className="text-muted-foreground leading-relaxed">
              13.1 During the term of the Agreement, Customer may access, export, or retrieve Customer Personal Data through the Service to the extent such functionality is made available.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              13.2 Upon termination or expiry of the Agreement, Wololo shall delete or return Customer Personal Data within a commercially reasonable period, unless retention is required by law.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              13.3 Notwithstanding Section 13.2, Wololo may retain:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 mt-2 ml-4">
              <li>backup copies for a limited period in accordance with its standard backup and disaster recovery processes;</li>
              <li>audit logs, security logs, rate-limit records, billing records, and similar operational records as reasonably required for security, fraud prevention, dispute defense, financial reporting, and legal compliance; and</li>
              <li>limited historical or referential records needed to preserve integrity of payroll, attendance, leave, compensation, notification, or audit history.</li>
            </ul>
            <p className="text-muted-foreground leading-relaxed mt-3">
              13.4 Any retained Customer Personal Data under Section 13.3 shall remain protected under this DPA for so long as it is retained.
            </p>
          </section>

          {/* Section 14 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">14. Special Categories and High-Risk Data</h2>
            <p className="text-muted-foreground leading-relaxed">
              14.1 The Service is not intended to require special categories of Personal Data or equivalent sensitive data unless the parties explicitly agree otherwise in writing.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              14.2 Customer shall not submit special category data, biometric data, medical records, government-issued identifiers, payment card data, or other regulated data to the Service except to the extent such submission is necessary for Customer's lawful use, Customer has identified an appropriate lawful basis, and the parties have agreed any additional safeguards that may be required.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              14.3 Customer acknowledges that free-text fields used by Customer users may contain sensitive information entered at Customer's direction or under Customer's control. Customer remains responsible for such use.
            </p>
          </section>

          {/* Section 15 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">15. Liability</h2>
            <p className="text-muted-foreground leading-relaxed">
              15.1 Liability arising under this DPA is subject to the exclusions and limitations of liability set out in the Agreement.
            </p>
            <p className="text-muted-foreground leading-relaxed mt-3">
              15.2 Nothing in this DPA limits either party's liability to the extent such limitation is prohibited by Applicable Data Protection Law.
            </p>
          </section>

          {/* Section 16 */}
          <section>
            <h2 className="text-xl font-semibold mb-3">16. Governing Law</h2>
            <p className="text-muted-foreground leading-relaxed">
              16.1 This DPA is governed by the governing law and dispute resolution provisions set out in the Agreement, except to the extent the SCCs or another mandatory transfer mechanism require otherwise.
            </p>
          </section>

          {/* Divider */}
          <div className="border-t border-border my-12" />

          {/* Annex I */}
          <section>
            <h2 className="text-2xl font-bold mb-6">Annex I — Details of Processing</h2>

            <h3 className="text-lg font-semibold mb-3">A. Subject Matter and Duration</h3>
            <p className="text-muted-foreground leading-relaxed">
              Wololo provides a business-to-business workforce management web application and related services. The Processing covers Customer Personal Data submitted to, stored in, generated by, or otherwise Processed through the Service for the duration of the Agreement and any limited post-termination retention period permitted by the Agreement and this DPA.
            </p>

            <h3 className="text-lg font-semibold mb-3 mt-6">B. Nature and Purpose of the Processing</h3>
            <p className="text-muted-foreground leading-relaxed">The nature and purpose of the Processing may include, as configured and used by Customer:</p>
            <ol className="list-decimal list-inside text-muted-foreground space-y-1 mt-2 ml-4">
              <li>account creation, user invitation, authentication, and profile administration;</li>
              <li>company and workplace configuration, including business settings, roles, permissions, and geofence setup;</li>
              <li>schedule and roster creation, shift publishing, shift offers, claims, swaps, and templates;</li>
              <li>attendance and timekeeping, including clock-in and clock-out records, approvals, comments, edit history, and anomaly or compliance flags;</li>
              <li>geolocation-based attendance checks, including storage of coordinates, accuracy, distance from workplace geofences, and related status determinations;</li>
              <li>leave policy administration, leave requests, approvals, balances, accruals, expirations, thresholds, and rollover history;</li>
              <li>compensation, payroll, payslip generation, payroll previews and exports, service charge allocation, and related line items and tax or deduction records configured by Customer;</li>
              <li>notifications, preference management, delivery queues, and in-app or email communications triggered by Customer workflows;</li>
              <li>audit logging, role changes, member lifecycle actions, and deletion or archival workflows;</li>
              <li>subscription, entitlement, seat-count, and billing administration; and</li>
              <li>support, troubleshooting, security monitoring, diagnostics, and fraud prevention related to the Service.</li>
            </ol>

            <h3 className="text-lg font-semibold mb-3 mt-6">C. Categories of Data Subjects</h3>
            <p className="text-muted-foreground leading-relaxed">Depending on Customer's use of the Service, Data Subjects may include:</p>
            <ol className="list-decimal list-inside text-muted-foreground space-y-1 mt-2 ml-4">
              <li>Customer's employees, staff, managers, supervisors, administrators, contractors, and other workforce members;</li>
              <li>invited users and prospective user accounts created by Customer within the Service;</li>
              <li>Customer personnel acting as billing, account, or technical contacts; and</li>
              <li>other individuals whose Personal Data Customer intentionally enters into workforce-related records within the Service.</li>
            </ol>

            <h3 className="text-lg font-semibold mb-3 mt-6">D. Categories of Personal Data</h3>
            <p className="text-muted-foreground leading-relaxed mb-4">Depending on Customer's configuration and use of the Service, Customer Personal Data may include:</p>

            <h4 className="text-base font-semibold mb-2">1. Identity and contact data</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mb-4">
              <li>first name, last name, preferred name;</li>
              <li>work email or other account email;</li>
              <li>address;</li>
              <li>mobile phone number;</li>
              <li>date of birth;</li>
              <li>emergency contact name and emergency contact phone;</li>
              <li>profile image / avatar URL.</li>
            </ul>

            <h4 className="text-base font-semibold mb-2">2. Account, role, and organizational data</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mb-4">
              <li>internal profile ID and user ID;</li>
              <li>company ID;</li>
              <li>role, role ID, permission assignments, status;</li>
              <li>FTE and weekly hours;</li>
              <li>invitation status and onboarding-related metadata.</li>
            </ul>

            <h4 className="text-base font-semibold mb-2">3. Scheduling and shift data</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mb-4">
              <li>shift assignments;</li>
              <li>scheduled shifts and templates;</li>
              <li>published roster data;</li>
              <li>shift notes, offers, claims, swaps, candidate responses, and approvals.</li>
            </ul>

            <h4 className="text-base font-semibold mb-2">4. Attendance and timekeeping data</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mb-4">
              <li>clock-in and clock-out timestamps;</li>
              <li>time-entry status, approval status, approver details, edit comments;</li>
              <li>time-entry comments, edit history, and status events;</li>
              <li>attendance-event flags such as unscheduled check-in, late arrival, early checkout, missed punch, or outside-geofence events.</li>
            </ul>

            <h4 className="text-base font-semibold mb-2">5. Geolocation and network data</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mb-4">
              <li>latitude and longitude collected during clock-in or clock-out events;</li>
              <li>accuracy values, captured-at timestamps, matched geofence IDs, geofence labels, distance-from-site measurements, and within-radius status;</li>
              <li>workplace geofence address, coordinates, radius, and place IDs configured by Customer;</li>
              <li>IP addresses recorded for clock-in and clock-out events.</li>
            </ul>

            <h4 className="text-base font-semibold mb-2">6. Leave and accrual data</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mb-4">
              <li>leave request details, leave type, dates, times, requested amount, balance-at-request, reasons, approvals, and status reasons;</li>
              <li>leave balances, policy assignments, accrual history, thresholds, rollover records, and related audit information.</li>
            </ul>

            <h4 className="text-base font-semibold mb-2">7. Compensation, payroll, and payslip data</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mb-4">
              <li>compensation rule assignments;</li>
              <li>payroll run metadata;</li>
              <li>payslips, earnings, deductions, net and gross values, taxable earnings, tax withheld, working hours, service charge allocations, and line-item notes;</li>
              <li>customer-configured deduction, allowance, overtime, bonus, salary, and tax logic.</li>
            </ul>

            <h4 className="text-base font-semibold mb-2">8. Communications and preference data</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mb-4">
              <li>notification payloads, severity, read state, delivery status;</li>
              <li>notification preferences, mutes, channels, and threshold overrides;</li>
              <li>transactional invitation email metadata and recipient details.</li>
            </ul>

            <h4 className="text-base font-semibold mb-2">9. Billing and subscription administration data</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mb-4">
              <li>subscription status and plan name;</li>
              <li>Stripe customer and subscription identifiers;</li>
              <li>seat-count and entitlement records;</li>
              <li>billing seat snapshots and webhook event records.</li>
            </ul>

            <h4 className="text-base font-semibold mb-2">10. Audit, security, and deletion-history data</h4>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mb-4">
              <li>member audit logs;</li>
              <li>role audit logs;</li>
              <li>security and delivery queue logs;</li>
              <li>limited historical identifiers or contact details preserved in historical records where required to preserve referential integrity or auditability after deletion workflows.</li>
            </ul>

            <h3 className="text-lg font-semibold mb-3 mt-6">E. Sensitive Processing Notes</h3>
            <ol className="list-decimal list-inside text-muted-foreground space-y-1 ml-4">
              <li>The Service may Process workplace geolocation data when Customer enables geolocation features.</li>
              <li>The Service may Process payroll, compensation, leave, and attendance data that can be sensitive in an employment context.</li>
              <li>The Service may support user-uploaded profile avatars; avatar content may be stored as a publicly retrievable object URL for display within the Service. Customer is responsible for determining whether such functionality is appropriate for its use case.</li>
              <li>The Service is not designed to require special category data unless separately agreed.</li>
            </ol>
          </section>

          {/* Divider */}
          <div className="border-t border-border my-12" />

          {/* Annex II */}
          <section>
            <h2 className="text-2xl font-bold mb-6">Annex II — Technical and Organizational Measures</h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              Wololo maintains technical and organizational measures designed to protect Customer Personal Data, including the following categories of measures:
            </p>

            <h3 className="text-lg font-semibold mb-3">1. Access control and identity management</h3>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mb-4">
              <li>user authentication through managed identity services;</li>
              <li>JWT-based authentication for Service access;</li>
              <li>role- and permission-based authorization checks for application features and administrative actions;</li>
              <li>separation between end-user access tokens and elevated server-side service credentials for backend operations.</li>
            </ul>

            <h3 className="text-lg font-semibold mb-3">2. Tenant segregation and logical access restrictions</h3>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mb-4">
              <li>logical segregation of customer records by company or tenant identifiers within the application data model;</li>
              <li>authorization logic designed to restrict access to records within the relevant company context;</li>
              <li>permission checks for sensitive operations such as billing, role management, member deletion, and administrative configuration.</li>
            </ul>

            <h3 className="text-lg font-semibold mb-3">3. Data minimization and feature-based control</h3>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mb-4">
              <li>Customer-administered configuration of workplace, geolocation, scheduling, leave, notification, and payroll features;</li>
              <li>optional geolocation processing triggered by Customer's enablement and user action;</li>
              <li>Customer-controlled use of profile fields, leave reasons, notifications, and other workflow data.</li>
            </ul>

            <h3 className="text-lg font-semibold mb-3">4. Network and application security</h3>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mb-4">
              <li>HTTPS/TLS-protected client and API traffic as supported by the deployed infrastructure;</li>
              <li>signed webhook verification for payment-provider callbacks;</li>
              <li>CORS handling for serverless endpoints;</li>
              <li>idempotency and duplicate-handling controls for selected backend workflows.</li>
            </ul>

            <h3 className="text-lg font-semibold mb-3">5. Logging, monitoring, and incident response support</h3>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mb-4">
              <li>application logging, webhook ledgers, audit records, queue state tracking, and operational error handling;</li>
              <li>monitoring and diagnostics tooling for application errors, performance tracing, and troubleshooting;</li>
              <li>security incident response processes, including Customer notification under Section 8 where required.</li>
            </ul>

            <h3 className="text-lg font-semibold mb-3">6. Availability and resilience</h3>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mb-4">
              <li>managed cloud infrastructure for database, authentication, storage, and serverless functions;</li>
              <li>queue-based and scheduled processing for selected background jobs;</li>
              <li>backup and recovery measures provided through infrastructure and service providers.</li>
            </ul>

            <h3 className="text-lg font-semibold mb-3">7. Data integrity and change tracking</h3>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mb-4">
              <li>audit and history tables for certain member, role, leave, attendance, payroll, and notification events;</li>
              <li>status tracking and update timestamps across core records;</li>
              <li>deletion workflows that mark records pending deletion and perform controlled cleanup of related records.</li>
            </ul>

            <h3 className="text-lg font-semibold mb-3">8. Deletion and retention controls</h3>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mb-4">
              <li>Customer-facing export and retrieval functionality where available within the Service;</li>
              <li>application workflows for archival, reactivation, and permanent deletion of workforce-member data;</li>
              <li>selective preservation of certain historical references and audit snapshots where needed for record integrity, security, dispute defense, or legal compliance.</li>
            </ul>

            <h3 className="text-lg font-semibold mb-3">9. Subprocessor management</h3>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4 mb-4">
              <li>contractual controls requiring Subprocessors to protect Personal Data consistent with the nature of their services;</li>
              <li>use of recognized infrastructure and service providers for hosting, authentication, storage, billing, email delivery, diagnostics, and mapping/location services.</li>
            </ul>

            <h3 className="text-lg font-semibold mb-3">10. Customer responsibilities relevant to security</h3>
            <p className="text-muted-foreground leading-relaxed mb-2">Customer remains responsible for:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-4">
              <li>setting appropriate internal permissions and role assignments;</li>
              <li>deciding whether to enable geolocation and other higher-sensitivity features;</li>
              <li>determining what data is entered into free-text fields;</li>
              <li>ensuring lawful notices and lawful bases for employee monitoring, payroll, leave, and location tracking; and</li>
              <li>evaluating whether avatar uploads or other user-visible content are suitable for Customer's own compliance requirements.</li>
            </ul>
          </section>

          {/* Divider */}
          <div className="border-t border-border my-12" />

          {/* Annex III */}
          <section>
            <h2 className="text-2xl font-bold mb-6">Annex III — Subprocessors and Service Providers</h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              The following service providers are reflected in the current Service implementation and may Process Customer Personal Data or support such Processing, depending on Customer's use of the Service:
            </p>

            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50">
                    <th className="text-left p-3 font-semibold text-foreground">Provider</th>
                    <th className="text-left p-3 font-semibold text-foreground">Role in Service</th>
                    <th className="text-left p-3 font-semibold text-foreground">Typical Data Involved</th>
                  </tr>
                </thead>
                <tbody className="text-muted-foreground">
                  <tr className="border-t border-border">
                    <td className="p-3 font-medium text-foreground">Supabase</td>
                    <td className="p-3">Cloud database, authentication, storage, realtime/serverless backend, file storage</td>
                    <td className="p-3">Account data, profiles, schedules, time entries, geolocation records, leave, payroll, notifications, billing metadata, avatars, audit data</td>
                  </tr>
                  <tr className="border-t border-border">
                    <td className="p-3 font-medium text-foreground">Stripe</td>
                    <td className="p-3">Subscription billing, customer records, payment-related webhook processing</td>
                    <td className="p-3">Billing contact data, subscription identifiers, plan and seat metadata, customer email</td>
                  </tr>
                  <tr className="border-t border-border">
                    <td className="p-3 font-medium text-foreground">Resend</td>
                    <td className="p-3">Transactional email delivery</td>
                    <td className="p-3">Invitation email addresses, reply-to data, invitation content and metadata</td>
                  </tr>
                  <tr className="border-t border-border">
                    <td className="p-3 font-medium text-foreground">Google Maps Platform</td>
                    <td className="p-3">Mapping and workplace/geofence support</td>
                    <td className="p-3">Workplace location data, geofence addresses and coordinates, map/location queries</td>
                  </tr>
                  <tr className="border-t border-border">
                    <td className="p-3 font-medium text-foreground">Sentry</td>
                    <td className="p-3">Application error monitoring, performance tracing, and replay-based diagnostics</td>
                    <td className="p-3">Technical telemetry, user interaction diagnostics, possible on-screen content or identifiers captured in error or replay context</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="text-muted-foreground leading-relaxed mt-4">
              Wololo may update or replace the above providers in accordance with Section 10 of this DPA.
            </p>
          </section>

          {/* Divider */}
          <div className="border-t border-border my-12" />

          {/* Execution */}
          <section>
            <h2 className="text-xl font-semibold mb-3">Execution</h2>
            <p className="text-muted-foreground leading-relaxed">
              By accepting the Terms, executing an Order Form, or otherwise using the Service under the Agreement, Customer agrees to this DPA as of the Effective Date.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default DataProcessingAddendum;
